import { useEffect, useState } from "react";
import apiClient from "../../core/config/api";
import PaymentResult from "./PaymentResult";
import { useHubSpotScript } from "../../utils/useHubSpotScript";

// Shown when Stripe reports the payment failed (the success page sends the
// customer here with the same ?session_id=). Same look as the success page.
// "Try Again" goes back to where that kind of purchase is made: credit
// top-ups / Pay As You Go -> Credits & Billing, plans -> Pricing.

type StatusResponse = {
  status?: string;
  failure_message?: string | null;
  is_topup?: boolean;
  amount?: string | number | null;
  currency?: string | null;
};

const formatAmount = (amount?: string | number | null, currency?: string | null) => {
  const value = Number(amount);
  if (!amount || Number.isNaN(value)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "USD").toUpperCase(),
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
};

const PaymentFailed = () => {
  useHubSpotScript();

  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const [details, setDetails] = useState<StatusResponse | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    apiClient
      .get<StatusResponse>(`/stripe-payment/status/${sessionId}`)
      .then(({ data }) => {
        if (!cancelled) setDetails(data);
      })
      .catch(() => {
        // Non-fatal: the page still explains what happened without it.
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const isTopUp = !!details?.is_topup;
  const amount = formatAmount(details?.amount, details?.currency);

  return (
    <PaymentResult
      variant="error"
      title="Payment Failed"
      message={
        "We couldn't complete your payment, so you have not been charged. " +
        (details?.failure_message
          ? `Your bank said: "${details.failure_message.replace(/\.$/, "")}". `
          : "This usually happens when the card is declined, doesn't have enough funds, " +
            "or the bank asks for extra verification. ") +
        "Please try again, or use a different card."
      }
      amount={amount}
      primary={{ label: "Try Again", to: isTopUp ? "/credit-billing" : "/pricing" }}
      secondary={{ label: "Back to Dashboard", to: "/dashboard" }}
    />
  );
};

export default PaymentFailed;
